import { Image, Text, View } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { payments } from "lobbie";
import { DATE_TIME_FORMATS } from "src/constants";
import { isEmailAutoGenerated } from "src/utils";
import styles, { pdfColors } from "../base/styles";
import { longStringProtection } from "../base/util";
import { formatPhone } from "../util";

interface IProps {
    invoiceDetails: payments.print.IInvoicePrintData;
    logoUrl?: string;
    business: payments.print.IBusinessData;
    receiptDetails?: payments.print.IPaymentRequest;
}

const InvoiceHeader: React.FC<IProps> = ({ invoiceDetails, logoUrl, business, receiptDetails }) => {
    const issueDate = dayjs(invoiceDetails.issueDateISO).format(
        DATE_TIME_FORMATS.Appointment.DateSpacedFormat,
    );
    let paymentDate;
    if (receiptDetails?.paymentDateISO) {
        paymentDate = dayjs(receiptDetails.paymentDateISO).format(
            DATE_TIME_FORMATS.Appointment.DateSpacedFormat,
        );
    }
    const dueDate = invoiceDetails.dueDateISO
        ? dayjs(invoiceDetails.dueDateISO).format("MM / DD / YYYY")
        : "";

    return (
        <View
            id="invoice-header"
            style={[{ borderBottom: "0.5px", borderBottomColor: pdfColors.Gray20 }, styles.pb8]}
            fixed
        >
            <View id="invoice-title" style={[styles.row, styles.w100, styles.pb8]}>
                <View style={[styles.row, styles.w100]}>
                    <Text
                        style={[
                            styles.col,
                            styles.textBlack,
                            styles.textH1,
                            styles.textBold,
                            styles.mr8,
                        ]}
                    >
                        {`${receiptDetails !== undefined ? "Receipt" : "Invoice"}`}
                    </Text>
                </View>
                {logoUrl && (
                    <Image id="brand-logo" style={[styles.col, styles.logo]} src={logoUrl} />
                )}
            </View>
            <View id="invoice-details-business" style={[styles.row]}>
                <View
                    id="invoice-details-col-1"
                    style={[styles.col, styles.mr16, styles.pb12, { gap: "16px" }]}
                >
                    {!receiptDetails ? (
                        <View style={[styles.row]}>
                            <View
                                id="invoice-details-col-1a"
                                style={[styles.col, styles.textGray65, styles.mr4]}
                            >
                                <Text style={[styles.mb4, styles.textBold, styles.textBlack]}>
                                    Invoice Number:
                                </Text>
                                <Text style={styles.mb4}>Date of Issue:</Text>
                                <Text style={styles.mb4}>Due Date:</Text>
                            </View>
                            <View id="invoice-details-col-1b" style={[styles.col]}>
                                <Text style={[styles.mb4, styles.textBold]}>
                                    {invoiceDetails.invoiceNumber}
                                </Text>
                                <Text style={styles.mb4}>{issueDate}</Text>
                                <Text style={styles.mb4}>{dueDate}</Text>
                            </View>
                        </View>
                    ) : (
                        <View style={[styles.row]}>
                            <View
                                id="invoice-details-col-1a"
                                style={[styles.col, styles.textGray65, styles.mr4]}
                            >
                                <Text style={[styles.mb4, styles.textBold, styles.textBlack]}>
                                    Payment Request ID:
                                </Text>
                                <Text style={styles.mb4}>Date Paid:</Text>
                                <Text style={styles.mb4}>Payment Method:</Text>
                            </View>
                            <View id="invoice-details-col-1b" style={[styles.col]}>
                                <Text style={[styles.mb4, styles.textBold]}>
                                    {`P-${receiptDetails.id}`}
                                </Text>
                                <Text style={styles.mb4}>{paymentDate}</Text>
                                <Text
                                    style={styles.mb4}
                                >{`${receiptDetails.paymentMethod?.brand} - ${receiptDetails.paymentMethod?.last4}`}</Text>
                            </View>
                        </View>
                    )}
                </View>
            </View>
            <View id="invoice-details-provider" style={[styles.row, styles.justBetween]}>
                <View
                    id="invoice-details-provider-col-1"
                    style={[styles.col, styles.mr16, { minWidth: "250px" }]}
                >
                    <View style={[styles.row, { gap: "16px" }]}>
                        <View
                            id="invoice-details-provider-col-1a"
                            style={[styles.col, { maxWidth: "140px" }]}
                        >
                            <Text style={[styles.mb4, styles.textBold]}>{business.name}</Text>
                            <Text style={styles.mb4}>{formatPhone(business.phoneNumber)}</Text>
                            <Text style={[styles.mb4]}>{business.address}</Text>
                        </View>
                        {invoiceDetails.provider && (
                            <View
                                id="invoice-details-provider-col-1b"
                                style={[styles.col, { maxWidth: "140px" }]}
                            >
                                <Text style={[styles.mb4, styles.textBold]}>{"Provider"}</Text>
                                <Text style={styles.mb4}>{invoiceDetails.provider.name}</Text>
                                {invoiceDetails.provider.npiNumber && (
                                    <Text
                                        style={styles.mb4}
                                    >{`NPI: #${invoiceDetails.provider.npiNumber}`}</Text>
                                )}
                            </View>
                        )}
                    </View>
                </View>
                <View
                    id="invoice-details-patient"
                    style={[
                        styles.col,
                        styles.mr16,
                        {
                            borderLeft: "0.5px",
                            borderLeftColor: pdfColors.Gray20,
                            minWidth: "211px",
                        },
                    ]}
                >
                    <View style={[styles.row]}>
                        <View id="invoice-details-patient-col-1" style={[styles.col, styles.pl12]}>
                            <Text style={[styles.mb4, styles.textBold]}>{"Bill to"}</Text>
                            <Text style={styles.mb4}>
                                {longStringProtection(invoiceDetails.accountPatient.name, 40)}
                            </Text>
                            <Text style={styles.mb4}>
                                {formatPhone(invoiceDetails.accountPatient.phone)}
                            </Text>
                            <Text style={styles.mb4}>
                                {!isEmailAutoGenerated(invoiceDetails.accountPatient.email)
                                    ? longStringProtection(invoiceDetails.accountPatient.email, 40)
                                    : ""}
                            </Text>
                            <Text style={[styles.mb4]}>
                                {invoiceDetails.accountPatient.address}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>
        </View>
    );
};

export default InvoiceHeader;
